/* eslint-disable no-unused-vars */
import { type Address, type Money, type Name, type MoneyInput } from '../common/common'
import { type PendingJointAcatTransfer, type RetirementContribution } from '../investments/investments'

export interface DisclosureInput {
  id?: string
  accepted: boolean
}

export interface EgmDisclosureInput {
  disclosureType: EgmDisclosureType
  version?: string
}

export enum EgmDisclosureType {
  //    Money Disclosure Types
  CHECKING_ACCOUNT_AGREEMENT = 'CHECKING_ACCOUNT_AGREEMENT',
  CHECKING_ACCOUNT_RATES_AND_FEES = 'CHECKING_ACCOUNT_RATES_AND_FEES',
  DEBIT_CARD_TERMS_AND_CONDITIONS = 'DEBIT_CARD_TERMS_AND_CONDITIONS',
  DIGITAL_WALLET_TERMS_AND_CONDITIONS = 'DIGITAL_WALLET_TERMS_AND_CONDITIONS',
  ELECTRONIC_FUNDS_TRANSFER_AGREEMENT = 'ELECTRONIC_FUNDS_TRANSFER_AGREEMENT',
  ELECTRONIC_SIGNATURE_CONSENT = 'ELECTRONIC_SIGNATURE_CONSENT',
  ACH_AUTHORIZATION = 'ACH_AUTHORIZATION',
  TAX_CERTIFICATION_FOR_US_PERSONS = 'TAX_CERTIFICATION_FOR_US_PERSONS',
  WIRE_TRANSFER_AGREEMENT = 'WIRE_TRANSFER_AGREEMENT',
  INVESTMENT_ADVISORY_AGREEMENT = 'INVESTMENT_ADVISORY_AGREEMENT',
  ADV_WRAP_FEE_BROCHURE = 'ADV_WRAP_FEE_BROCHURE',
  CUSTOMER_RELATIONSHIP_SUMMARY = 'CUSTOMER_RELATIONSHIP_SUMMARY',
  STANDING_LETTER_OF_AUTHORIZATION = 'STANDING_LETTER_OF_AUTHORIZATION',
  ADVISOR_PRIVACY_NOTICE = 'ADVISOR_PRIVACY_NOTICE',
  ADVISOR_PRIVACY_POLICY = 'ADVISOR_PRIVACY_POLICY',
  EVERGREEN_PRIVACY_NOTICE = 'EVERGREEN_PRIVACY_NOTICE',
  EVERGREEN_GENERAL_PRIVACY_POLICY = 'EVERGREEN_GENERAL_PRIVACY_POLICY',
  //    Wealth Disclosure Types
  DEPOSIT_AGREEMENT = 'DEPOSIT_AGREEMENT',
}

export interface Transaction {
  id?: string
  display: TransactionDisplay
  status: TransactionStatus
}

export interface TransactionParty {
  abaId?: string
  companyId?: string
  institutionName?: string
}

export interface BankingTransaction extends Transaction {
  id?: string
  display: TransactionDisplay
  status: TransactionStatus
  method?: TransactionMethod
  counterParty?: TransactionParty
  originator?: TransactionParty
  settledDate?: string
  threadId?: string
  transactionCode?: number
  eft?: Eft
  ach?: Ach
  note?: string
}

// // @Deprecated
export interface TransactionDisplay {
  date: string
  amount: Money
  description: string
  displayName: string
}

export enum TransactionMethod {
  UNKNOWN = 'UNKNOWN',
  TRANSFER = 'TRANSFER',
  DEBIT_CARD = 'DEBIT_CARD',
  INTEREST = 'INTEREST',
  ADJUSTMENT = 'ADJUSTMENT',
  FEE = 'FEE',
  ACH = 'ACH',
  WIRE_TRANSFER = 'WIRE_TRANSFER',
  ADMINISTRATIVE = 'ADMINISTRATIVE',
  INVESTMENTS = 'INVESTMENTS',
  BOOK_TRANSFER = 'BOOK_TRANSFER',
}

export enum TransactionStatus {
  UNKNOWN = 'UNKNOWN',
  PENDING = 'PENDING',
  INITIATED = 'INITIATED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

export interface CreateBankInvestmentTransferInput {
  bankAccountAlias: TransferAccountInput
  investmentAccountAlias: TransferAccountInput
  money: MoneyInput
  disclosures: DisclosureInput[]
  liquidateAccount?: boolean
}

export interface TransferAccountInput {
  alias: number
  isTo: boolean
}

export interface CreateBankInvestmentTransferOutput
  extends
    CreateBankInvestmentTransfer,
    CreateBankInvestmentTransferError,
    InvestmentMinimumDepositNotMetError,
    AnnualRetirementContributionExceededError {
  __typename: string
}
export interface CreateBankInvestmentTransfer {
  id?: string
  marketStatus: MarketStatus
}

export interface CreateInvestmentAchTransferInput {
  achAccountAlias: TransferAccountInput
  investmentAccountAlias: TransferAccountInput
  money: MoneyInput
  disclosures: DisclosureInput[]
}

export interface CreateInvestmentAchTransferOutput
  extends CreateInvestmentAchTransfer, CreateInvestmentAchTransferError, AnnualRetirementContributionExceededError {
  __typename: string
}

export interface CreateInvestmentAchTransfer {
  id: string
  status: InvestmentAchStatus
}

export enum InvestmentAchStatus {
  PENDING = 'PENDING',
  SUBMITTED = 'SUBMITTED',
  POSTPONED = 'POSTPONED',
}
export interface CreateInvestmentAchTransferError {
  errorType: CreateInvestmentAchTransferErrorType
}

export enum CreateInvestmentAchTransferErrorType {
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  TRANSFER_LIMIT_BREACHED = 'TRANSFER_LIMIT_BREACHED',
  AMOUNT_REQUESTED_REQUIRES_LIQUIDATION = 'AMOUNT_REQUESTED_REQUIRES_LIQUIDATION',
  BAD_REQUEST = 'BAD_REQUEST',
  LINKED_ACCOUNT_ERROR = 'LINKED_ACCOUNT_ERROR',
  TRANSFER_FAILED = 'TRANSFER_FAILED',
}

export enum MarketStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  CLOSING_SOON = 'CLOSING_SOON',
}

export interface InvestmentMinimumDepositNotMetError {
  minimumDepositRequirementAmount?: number
}

export interface AnnualRetirementContributionExceededError {
  retirementContribution?: RetirementContribution
}

export interface CreateBankInvestmentTransferError {
  errorType?: CreateBankInvestmentTransferErrorType
}

export enum CreateBankInvestmentTransferErrorType {
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  AMOUNT_REQUESTED_REQUIRES_LIQUIDATION = 'AMOUNT_REQUESTED_REQUIRES_LIQUIDATION',
  MINIMUM_DEPOSIT_NOT_MET = 'MINIMUM_DEPOSIT_NOT_MET', // this is the minimum for a deposit not the account minimum
  PENDING_TRANSFER_ALREADY_EXISTS = 'PENDING_TRANSFER_ALREADY_EXISTS',
  BAD_REQUEST = 'BAD_REQUEST',
  // INTERNAL_ERROR = 'INTERNAL_ERROR', @deprecated
  // DAILY_TRANSFER_ALREADY_EXECUTED = 'DAILY_TRANSFER_ALREADY_EXECUTED', @deprecated
}

export interface CreateAchOrderInput {
  achAccountAlias: number
  orderType: OrderType //// Should Only be PAYMENT_ORDER or COLLECTION_ORDER
  money: MoneyInput
  achAuthTerms: DisclosureInput
}

export interface CreateAchOrderInputV2 {
  accountAlias: number
  achAccountAlias: number
  orderType: OrderType // // Should Only be PAYMENT_ORDER or COLLECTION_ORDER
  money: MoneyInput
  achAuthTerms: DisclosureInput
}

export interface CreateAchOrderOutput
  extends CreateAchOrderSuccess, CreateAchOrderError, CreateAchOrderLimitError, BankingMinimumDepositNotMetError {
  __typename: string
}

export interface CreateAchOrderSuccess {
  id?: string
}

export interface CreateAchOrderError {
  type: CreateAchOrderErrorType
}

export interface CreateAchOrderLimitError {
  limitType: AchOrderLimitType
  limit: number
  remaining: number
}

export interface BankingMinimumDepositNotMetError {
  minimumDepositRequirementAmount: number
}

export enum AchOrderLimitType {
  SINGLE = 'SINGLE',
  MONTHLY = 'MONTHLY',
  DAILY = 'DAILY',
}

export enum CreateAchOrderErrorType {
  UNABLE_TO_CREATE_ACH_ORDER = 'UNABLE_TO_CREATE_ACH_ORDER',
  LINKED_ACH_ACCOUNT_NOT_ACTIVE = 'LINKED_ACH_ACCOUNT_NOT_ACTIVE',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  // @deprecated INADEQUATE_DEPOSIT_AMOUNT
  BAD_REQUEST = 'BAD_REQUEST',
  NOT_AVAILABLE_FOR_ACCOUNT = 'NOT_AVAILABLE_FOR_ACCOUNT',
}

export interface CreateBankInternalTransferInput {
  fromAlias: number
  toAlias: number
  amount: MoneyInput
}

export interface CreateBankInternalTransferOutput extends CreateBankInternalTransfer, CreateBankInternalTransferError {
  __typename: string
}

export interface CreateBankInternalTransfer {
  id?: string
}

export interface CreateBankInternalTransferError {
  errorType: CreateBankInternalTransferErrorType
}

export enum CreateBankInternalTransferErrorType {
  FAILED_TO_CREATE_TRANSFER = 'FAILED_TO_CREATE_TRANSFER',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  BAD_REQUEST = 'BAD_REQUEST',
}

export enum OrderType {
  PAYMENT_ORDER = 'PAYMENT_ORDER', // Outbound Order
  COLLECTION_ORDER = 'COLLECTION_ORDER', // Inbound Order
}

export interface PendingTransaction extends Transaction {
  id?: string
  status: TransactionStatus
  method: TransactionMethod
  display: TransactionDisplay
  note?: string
  positionAccountNumber: string
  positionId: string
  reason?: string
  holdType: string
  eft?: Eft
}

export interface Eft {
  cardToken: string
  acceptor: string
  acqNetCode: string
  acqName: string
  merchantCode: number
  location: TransactionLocation
}

export interface TransactionLocation {
  city: string
  country: string
  postalCode: string
  region: string
  street: string
}

export interface Ach {
  amount: Money
  transactionDate: Date
  description: string
  finInstOrMerchantName: string
  routingNumber: string
  accountName: string
  note: string
}

export enum WireDirection {
  OUTGOING = 'OUTGOING',
  INCOMING = 'INCOMING',
  RETURN_OF_OUTGOING = 'RETURN_OF_OUTGOING',
  UNKNOWN = 'UNKNOWN',
}

export interface WireTrn {
  direction: WireDirection
  institutionName: string
  imad?: string
}

export interface TransactionReceipt {
  transactionId: string
  positionId: string
  accountGroup: string
  accountNumber: string
  positionAccountNumber: string
}

export interface TransactionsOutput {
  bank: BankTransaction[]
}

// Transaction V2
export interface BankTransaction {
  id?: string
  displayName: string
  date: string
  amount: Money
  status: TransactionStatus
  isProcessing: boolean
  pendingBanner?: PendingBanner
  method: TransactionMethod
  label: string
  eft?: Eft
  ach?: Ach
  wire?: WireTrn
  note?: string
}

export enum PendingBanner {
  TRANSFER_IN = 'TRANSFER_IN',
  TRANSFER_OUT = 'TRANSFER_OUT',
  CARD_PURCHASE = 'CARD_PURCHASE',
  CARD_PURCHASE_REFUND = 'CARD_PURCHASE_REFUND',
  P2P_OUT = 'P2P_OUT',
}

export interface NameInput {
  firstName: string
  lastName: string
}

export interface AddressInput {
  line1: string
  line2?: string
  city: string
  region: string // PROVINCE, STATE, COUNTY, ETC
  postalCode: string
  country: string // Using COUNTRY CODES ALPHA-2
}

export interface WireBeneficiaryInput {
  name?: NameInput
  businessName?: string
  address: AddressInput
  beneficiaryRoutingNumber: string
  beneficiaryAccountNumber: string
  intermediaryRoutingNumber?: string
}

export interface PendingWireInput {
  id?: string
  money: MoneyInput
  to?: WireBeneficiaryInput
  reference?: string
}

export interface SubmitWireInput {
  id?: string
  money: MoneyInput
  to: WireBeneficiaryInput
  reference: string
  disclosure: EgmDisclosureInput
}

export interface PendingWireInputV2 {
  id?: string
  accountAlias: number
  money: MoneyInput
  to?: WireBeneficiaryInput
  reference?: string
}

export interface SubmitWireInputV2 {
  id: string
  accountAlias: number
  money: MoneyInput
  to: WireBeneficiaryInput
  reference: string
  disclosure: EgmDisclosureInput
}

export interface submitWireInputV3 {
  accountAlias: number
  money: MoneyInput
  to: WireBeneficiaryInput
  reference: string
  disclosure: EgmDisclosureInput
}

export interface WireBeneficiary {
  name?: Name
  businessName?: string
  address: Address
  beneficiaryRoutingNumber: string
  beneficiaryAccountNumber: string
  intermediaryRoutingNumber?: string
}

export interface PendingWireOutput extends Wire, PendingWireError {
  __typename: string
}

export interface SubmitWireOutput extends Wire, SubmitWireError {
  __typename: string
}

export interface Wire {
  id: string
  money: Money
  to?: WireBeneficiary
  reference?: string
  isSubmitted: boolean
  status: WireStatus
  accountAlias: number
}

export interface WireV3 {
  money: Money
  to?: WireBeneficiary
  reference?: string
  isSubmitted: boolean
  status: WireStatus
  accountAlias: number
}

export interface SubmitWireOutputV3 extends WireV3, SubmitWireError {
  __typename: string
}

export enum WireStatus {
  PENDING = 'PENDING',
  SUBMITTED = 'SUBMITTED',
}

export interface PendingWireError {
  errorType?: PendingWireErrorType
}

export enum PendingWireErrorType {
  INVALID_AMOUNT_REQUESTED = 'INVALID_AMOUNT_REQUESTED',
  INVALID_BENEFICIARY_ROUTING_NUMBER = 'INVALID_BENEFICIARY_ROUTING_NUMBER',
  INVALID_INTERMEDIARY_ROUTING_NUMBER = 'INVALID_INTERMEDIARY_ROUTING_NUMBER',
  NSF = 'NSF',
  BAD_REQUEST = 'BAD_REQUEST',
  NOT_AVAILABLE_FOR_ACCOUNT = 'NOT_AVAILABLE_FOR_ACCOUNT',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}

export enum SubmitWireErrorType {
  INVALID_DISCLOSURE = 'INVALID_DISCLOSURE',
  INCOMPLETE_WIRE = 'INCOMPLETE_WIRE',
  BAD_REQUEST = 'BAD_REQUEST',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  INVALID_AMOUNT_REQUESTED = 'INVALID_AMOUNT_REQUESTED',
  INVALID_BENEFICIARY_ROUTING_NUMBER = 'INVALID_BENEFICIARY_ROUTING_NUMBER',
  INVALID_INTERMEDIARY_ROUTING_NUMBER = 'INVALID_INTERMEDIARY_ROUTING_NUMBER',
  NOT_AVAILABLE_FOR_ACCOUNT = 'NOT_AVAILABLE_FOR_ACCOUNT',
  NSF = 'NSF',
}

export interface SubmitWireError {
  errorType?: SubmitWireErrorType
}

export interface InvestmentTransferValidationInput {
  alias: number
  isTo: boolean
}

export interface InvestmentTransferValidation {
  __typename: string
  _: boolean
}

export interface InvestmentTransferValidationError {
  __typename: string
  errorType: InvestmentTransferValidationErrorType
}

export enum InvestmentTransferValidationErrorType {
  PENDING_TRANSFER_ALREADY_EXISTS = 'PENDING_TRANSFER_ALREADY_EXISTS',
  BAD_REQUEST = 'BAD_REQUEST',
}

export type InvestmentTransferValidationOutput = InvestmentTransferValidation | InvestmentTransferValidationError

export interface BankingInstitution {
  __typename: 'BankingInstitution'
  bankName: string
}

export enum GetBankInstitutionNameErrorType {
  UNABLE_TO_FIND_INSTITUTION = 'UNABLE_TO_FIND_INSTITUTION',
}

export interface GetBankInstitutionNameError {
  __typename: 'GetBankInstitutionNameError'
  type: GetBankInstitutionNameErrorType
}

export interface FileUploadSuccess {
  filePath: string
  success: boolean
}

export interface FileUploadError {
  error: string
  message: string
}

export interface SubmitAcatDocumentsInput {
  accountAlias: number
  filePaths: string[]
  disclosures: DisclosureInput[]
}

export interface SubmitAcatDocumentsSuccess {
  __typename: 'SubmitAcatDocumentsSuccess'
  isSuccessful: boolean
  pendingJointAcatTransfer?: PendingJointAcatTransfer
}

export enum SubmitAcatDocumentsErrorType {
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  INVALID_FILE_PATH = 'INVALID_FILE_PATH',
  ACAT_NOT_ALLOWED_FOR_ACCOUNT_TYPE = 'ACAT_NOT_ALLOWED_FOR_ACCOUNT_TYPE',
  REQUIRED_DISCLOSURE_NOT_ACKNOWLEDGED = 'REQUIRED_DISCLOSURE_NOT_ACKNOWLEDGED',
}

export interface SubmitAcatDocumentsError {
  __typename: 'SubmitAcatDocumentsError'
  errorType: SubmitAcatDocumentsErrorType
}

export type SubmitAcatDocumentsOutput = SubmitAcatDocumentsSuccess | SubmitAcatDocumentsError

export interface InvestmentWireBeneficiaryInput {
  accountNumber: string
  accountTitle: string
  accountAddress: AddressInput
}

export interface InvestmentWireIntermediaryInput {
  accountNumber: string
  accountTitle: string
  accountAddress: AddressInput
}

export interface InvestmentWireRecipientBankInput {
  bankId: string
}

export type SubmitInvestmentWireOutput = SubmitInvestmentWireSuccess | SubmitInvestmentWireOutputError

export interface SubmitInvestmentWireSuccess {
  __typename: 'SubmitInvestmentWireSuccess'
  id: string
}

export interface SubmitInvestmentWireOutputError {
  __typename: 'SubmitInvestmentWireOutputError'
  errorType: SubmitInvestmentWireOutputErrorType
  message?: string
}

export enum SubmitInvestmentWireOutputErrorType {
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  BAD_REQUEST = 'BAD_REQUEST',
  LIQUIDATION_NOT_ALLOWED = 'LIQUIDATION_NOT_ALLOWED',
}

export interface SubmitInvestmentWireInput {
  accountAlias: number
  money: MoneyInput
  beneficiary: InvestmentWireBeneficiaryInput
  recipientBank: InvestmentWireRecipientBankInput
  disclosure: EgmDisclosureInput
}
